<template>
  <div class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" @click.self="$emit('close')">
    <div class="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[92vh] overflow-y-auto">
      <div class="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-800">
          {{ initial.id ? `Edit Task "${initial.name}" (new version)` : 'New Task' }}
        </h3>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-700">&times;</button>
      </div>

      <div class="p-5 space-y-4">
        <div class="grid grid-cols-3 gap-3">
          <div class="col-span-2">
            <label class="block text-[11px] font-medium text-gray-600 mb-1">Name</label>
            <input
              v-model="editing.name"
              type="text"
              :disabled="!!initial.id"
              class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark disabled:bg-gray-50 disabled:text-gray-500"
              placeholder="e.g. 'ExtractCandidates'"
              @input="dirty = true"
            />
            <p v-if="initial.id" class="text-[10px] text-gray-400 mt-1">
              Name is the version-spanning identifier. Create a new Task to change it.
            </p>
          </div>
          <div class="flex items-end gap-2">
            <div class="text-[11px] text-gray-600">
              {{ initial.id ? 'Saves as a new version' : 'v1 on save' }}
            </div>
          </div>
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Description</label>
          <textarea
            v-model="editing.description"
            rows="2"
            class="w-full text-sm px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-dark resize-none"
            placeholder="What this Task does — shown in the UI"
            @input="dirty = true"
          />
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">
            Instruction (markdown)
          </label>
          <textarea
            v-model="editing.instruction_text"
            rows="14"
            spellcheck="false"
            class="w-full text-xs px-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-gray-400 resize-vertical leading-relaxed font-mono"
            placeholder="Tell the LLM what to do…"
            @input="dirty = true"
          />
          <p class="text-[10px] text-gray-400 mt-1">
            Freeform markdown. Composable templates land with SAT migration.
          </p>
        </div>

        <div>
          <label class="block text-[11px] font-medium text-gray-600 mb-1">Output Schema (JSON Schema)</label>
          <textarea
            v-model="outputSchemaStr"
            rows="12"
            spellcheck="false"
            class="w-full text-[11px] px-2 py-1.5 border border-gray-200 rounded font-mono bg-white focus:outline-none focus:border-gray-400 resize-vertical leading-relaxed"
            placeholder='{
  "type": "object",
  "properties": { "items": { "type": "array", "items": { "type": "object" } } },
  "required": ["items"]
}'
            @input="dirty = true; schemaError = null"
          />
          <p v-if="schemaError" class="text-[10px] text-red-500 mt-1">{{ schemaError }}</p>
          <p class="text-[10px] text-gray-400 mt-1">
            Add <code>"description"</code> fields per property — the LLM reads them as guidance.
          </p>
        </div>

        <div class="flex items-center gap-2">
          <input id="task-active" type="checkbox" v-model="editing.active" @change="dirty = true" />
          <label for="task-active" class="text-xs text-gray-700">Active</label>
        </div>
      </div>

      <div class="px-5 py-3 border-t border-gray-200 flex items-center gap-2.5">
        <span class="text-[11px] mr-auto" :class="dirty ? 'text-blue-600' : 'text-gray-400'">
          {{ dirty ? 'Unsaved changes' : 'Up to date' }}
        </span>
        <button @click="$emit('close')" class="text-xs px-3 py-1.5 rounded border border-gray-200 text-gray-600 bg-white hover:bg-gray-50">Cancel</button>
        <button @click="save" :disabled="!dirty || saving" class="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40">
          {{ saving ? 'Saving…' : (initial.id ? 'Save as new version' : 'Create') }}
        </button>
      </div>

      <div v-if="saveError" class="px-5 py-2 border-t border-gray-200 text-xs text-red-500">{{ saveError }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE = 'api/intelligence/'

export default {
  name: 'TaskModal',
  props: {
    initial: { type: Object, default: () => ({}) },
  },
  emits: ['close', 'saved'],

  data() {
    return {
      editing: {
        name: this.initial.name || '',
        description: this.initial.description || '',
        instruction_text: this.initial.instruction_text || '',
        active: this.initial.active ?? true,
      },
      outputSchemaStr: JSON.stringify(this.initial.output_schema || {}, null, 2),
      schemaError: null,
      saveError: null,
      saving: false,
      dirty: false,
    }
  },

  methods: {
    async save() {
      this.saveError = null
      this.schemaError = null
      if (!this.editing.name) { this.saveError = 'Name required'; return }
      if (!this.editing.instruction_text) { this.saveError = 'Instruction required'; return }

      let output_schema
      try {
        output_schema = JSON.parse(this.outputSchemaStr || '{}')
      } catch {
        this.schemaError = 'Invalid JSON'
        return
      }

      this.saving = true
      try {
        // Tasks are POST-only — every save creates a new versioned row.
        const { data } = await axios.post(`${API_BASE}tasks/`, {
          name: this.editing.name,
          description: this.editing.description || '',
          instruction_text: this.editing.instruction_text,
          output_schema,
          active: this.editing.active,
        })
        this.$emit('saved', data)
      } catch (e) {
        this.saveError = e.response?.data?.detail
          || JSON.stringify(e.response?.data || {})
          || 'Save failed'
      } finally {
        this.saving = false
      }
    },
  },
}
</script>
